/**
 * Prompt options for chat Tools dropdown.
 * Tool selection = "use this capability for the next message"; picking an option prefills the input.
 * Add or edit entries to change what appears when user selects a tool (e.g. Perplexity Search).
 */

export interface ToolPromptOption {
  id: string;
  label: string;
  prompt: string;
}

export interface ChatToolWithPrompts {
  toolId: string;
  label: string;
  promptOptions: ToolPromptOption[];
}

export const CHAT_TOOL_PROMPT_OPTIONS: ChatToolWithPrompts[] = [
  {
    toolId: "perplexity_search",
    label: "Quick Search",
    promptOptions: [
      {
        id: "competitors_nearby",
        label: "What are customers saying about competitors near me?",
        prompt:
          "Search for recent customer reviews and feedback about competitors in my industry near my location. Summarize what customers love and hate about them.",
      },
      {
        id: "promotions",
        label: "What promotions are competitors running right now?",
        prompt:
          "Find current promotions, deals, and special offers that my competitors are running right now. List the best ones I should be aware of.",
      },
      {
        id: "lead_ideas",
        label: "Find new lead ideas for my business",
        prompt:
          "Find 10 new lead generation ideas specifically for my type of business that I can act on this week.",
      },
      {
        id: "post_ideas",
        label: "What should I post about this week?",
        prompt:
          "Based on current trends and seasonal relevance, what should I post about on social media this week for my business? Give me 5 specific ideas.",
      },
    ],
  },
  {
    toolId: "deep_research",
    label: "Deep Research",
    promptOptions: [
      {
        id: "market_analysis",
        label: "Full market analysis for my industry",
        prompt:
          "Do a thorough market analysis for my industry — key trends, top competitors, customer pain points, and opportunities I should be targeting right now.",
      },
      {
        id: "competitor_deep_dive",
        label: "Deep dive on a competitor",
        prompt:
          "Do a deep research report on my top competitor — their positioning, pricing, reviews, recent news, and weaknesses I can exploit.",
      },
      {
        id: "content_strategy",
        label: "Research content strategy for my niche",
        prompt:
          "Research what content is performing best in my industry right now. What topics, formats, and platforms are driving the most engagement?",
      },
      {
        id: "local_market",
        label: "Research my local market",
        prompt:
          "Research my local market thoroughly — demand for my services, who's competing, what customers are searching for, and gaps I can fill.",
      },
    ],
  },
  {
    toolId: "social_posts",
    label: "Social Posts",
    promptOptions: [
      {
        id: "all_platforms",
        label: "Posts for all platforms",
        prompt:
          "Create engaging social media posts for all my platforms based on my business and brand.",
      },
      {
        id: "repurpose",
        label: "Repurpose existing content",
        prompt:
          "Repurpose my existing content into fresh social media posts optimized for each platform.",
      },
      {
        id: "promotional",
        label: "Promotional post",
        prompt:
          "Write a compelling promotional post for my product or service that drives conversions.",
      },
      {
        id: "engagement",
        label: "Boost engagement",
        prompt:
          "Create an engaging question or poll post to boost audience interaction across my platforms.",
      },
    ],
  },
  {
    toolId: "blog",
    label: "Blog",
    promptOptions: [
      {
        id: "answer_question",
        label: "Answer a customer question",
        prompt:
          "Write a helpful blog post that answers a common question my customers ask about my business. Pick a relevant question and explain it clearly for someone new to the topic.",
      },
      {
        id: "how_to",
        label: "How-to guide",
        prompt:
          "Write a step-by-step how-to blog post that teaches my audience how to do something useful related to my business or industry. Use clear, numbered steps.",
      },
      {
        id: "promote_offering",
        label: "Promote a product or service",
        prompt:
          "Write a blog post that highlights one of my products or services, explains how it helps customers, and ends with a clear call to action.",
      },
      {
        id: "seo_post",
        label: "Rank on Google (SEO post)",
        prompt:
          "Write an SEO-optimized blog post targeting a topic or keyword relevant to my business, with a search-friendly title and clear H2 sections, to help it rank on Google.",
      },
    ],
  },
  {
    toolId: "outreach_email",
    label: "Outreach Email",
    promptOptions: [
      {
        id: "networking_1to1",
        label: "Ask for a 1-2-1 after a networking event",
        prompt:
          "I met these people at a networking event and want to follow up asking for a 1-2-1. Draft a personalized outreach email template with merge fields for first name and company.",
      },
      {
        id: "event_followup",
        label: "Follow up with contacts from an event",
        prompt:
          "Draft a follow-up email template for people I met at an event this past week, reminding them who I am and suggesting we connect.",
      },
      {
        id: "referral_partner",
        label: "Reach out to a potential referral partner",
        prompt:
          "Draft an outreach email template asking a potential referral partner to grab a quick call and explore sending each other referrals.",
      },
      {
        id: "reconnect",
        label: "Reconnect with a past contact",
        prompt:
          "Draft a short, friendly outreach email template to reconnect with a past contact I haven't talked to in a while and see if they're open to catching up.",
      },
    ],
  },
  {
    toolId: "create_image_batch",
    label: "Bulk Images",
    promptOptions: [
      {
        id: "campaign_10",
        label: "10-image promo campaign",
        prompt:
          "Create a campaign of 10 images on one theme for my current promotion — same visual style, a different offer or message on each.",
      },
      {
        id: "campaign_5",
        label: "5 posts on one theme",
        prompt:
          "Create 5 images on a single theme for my social feed this week — one cohesive look, a different headline on each.",
      },
      {
        id: "story_set",
        label: "Story background set",
        prompt:
          "Create a set of 5 story-format background images in my brand style, each with a different short line of copy.",
      },
      {
        id: "product_range",
        label: "One image per product",
        prompt:
          "Create one image per product I sell — same layout and brand style across the set, the product name on each.",
      },
    ],
  },
  {
    toolId: "create_image",
    label: "Create Image",
    promptOptions: [
      {
        id: "3d",
        label: "3D Illustration",
        prompt:
          "Create a 3D illustration for my brand that I can use for social media and marketing.",
      },
      {
        id: "infographic",
        label: "Infographic",
        prompt:
          "Create a visually appealing infographic that explains key information about my business.",
      },
      {
        id: "banner",
        label: "Social Media Banner",
        prompt:
          "Design a professional social media banner for my brand that works across all platforms.",
      },
      {
        id: "photorealistic",
        label: "Photorealistic Image",
        prompt:
          "Generate a photorealistic image that showcases my brand or product in a compelling way.",
      },
      {
        id: "edit",
        label: "Edit an existing image",
        prompt:
          "I want to edit an existing image. Upload the image using the attachment button, then describe what changes you'd like made.",
      },
    ],
  },
];

/** Get prompt options for a tool ID, if any. */
export function getPromptOptionsForTool(
  toolId: string
): ChatToolWithPrompts | undefined {
  return CHAT_TOOL_PROMPT_OPTIONS.find((t) => t.toolId === toolId);
}
